import React from 'react';
import PropTypes from 'prop-types';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import cx from 'classnames';

import {SimpleCheckIcon, InformationIcon, ShieldIcon} from './svg';
import Button, {ButtonSizes, ButtonTypes} from './ButtonNew';

export default function PlanCard(props) {
  const {
    className,
    highlighted,
    name,
    features,
    price,
    priceSecondaryText,
    discountedPrice,
    onClick,
    href,
    target,
    cta,
    disabled,
    badge,
    freeTrial,
    saleText,
    description,
  } = props;

  const classes = cx(
    'flex flex-col p-10 rounded-3xl bg-white border-gray-300 max-w-xs flex-shrink-0', {
      'border-2 border-indigo-700': highlighted,
      border: !highlighted,
    }, className,
  );

  return (
    <div className={classes} style={props.style}>
      <div className="flex justify-between items-center">
        <div className="text-xl font-semibold text-black">
          {name}
        </div>
        {badge && (
          <div
            className="text-xs font-medium uppercase rounded-xl px-3 py-1"
            style={{
              backgroundColor: badge.backgroundColor,
              color: badge.textColor,
            }}
          >
            {badge.text}
          </div>
        )}
      </div>

      <div className="mt-4 text-sm text-gray-600 mb-7">
        {description}
      </div>

      {discountedPrice || freeTrial ? (
        <div className="-mt-5">
          <span className="ml-1 text-green-500 text-sm line-through font-bold">
            {price}
            &nbsp;
            {priceSecondaryText}
          </span>
          <div className="flex items-baseline gap-x-2">
            <span className="text-4xl font-semibold tracking-tight text-gray-900">
              {discountedPrice}
            </span>
            <span className="text-sm font-semibold text-gray-600">
              {priceSecondaryText}
            </span>
            {freeTrial && <span className="text-4xl font-semibold text-green-400">Free</span>}
          </div>
        </div>
      ) : (
        <div className="flex items-baseline gap-x-2">
          <span className="text-4xl font-semibold tracking-tight text-gray-900">{price}</span>
          <span className="text-sm font-semibold text-gray-600">{priceSecondaryText}</span>
        </div>
      )}

      <Button
        buttonType={highlighted ? ButtonTypes.PRIMARY : ButtonTypes.SECONDARY}
        size={ButtonSizes.LARGE}
        className="mt-6 text-base"
        onClick={onClick}
        href={href}
        target={target}
        disabled={disabled}
      >
        {cta}
      </Button>


      <ul className="mt-8 space-y-3 text-sm text-gray-600">
        {features.map((f) => (
          <li className="flex items-start">
            <SimpleCheckIcon className="h-5 w-5 text-indigo mr-3 flex-shrink-0" />
            <div className="flex flex-wrap items-center">
              <span>{f.label}</span>
              {f.tooltip && (
              <OverlayTrigger
                placement="right"
                overlay={<Tooltip>{f.tooltip}</Tooltip>}
              >
                <span>
                  <InformationIcon
                    className="cursor-pointer mx-1 h-4 w-4 sm:flex"
                    fill="#999999"
                  />
                </span>
              </OverlayTrigger>
              )}
            </div>
          </li>
        ))}
      </ul>
      {saleText && (
        <span className="mt-2 flex items-center justify-center font-medium text-gray-500 text-sm">
          <ShieldIcon className="mr-1 h-5 w-5" stroke="#888888" />
          {saleText}
        </span>
      )}
    </div>
  );
}

PlanCard.propTypes = {
  className: PropTypes.string,
  highlighted: PropTypes.bool,
  name: PropTypes.string.isRequired,
  features: PropTypes.arrayOf(PropTypes.string).isRequired,
  price: PropTypes.string.isRequired,
  priceSecondaryText: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  href: PropTypes.string,
  target: PropTypes.string,
  cta: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  style: PropTypes.shape(),
  badge: PropTypes.shape({
    backgroundColor: PropTypes.string,
    color: PropTypes.string,
    text: PropTypes.string,
    textColor: PropTypes.string,
  }),
};

PlanCard.defaultProps = {
  className: null,
  highlighted: false,
  priceSecondaryText: null,
  disabled: false,
  badge: null,
  style: null,
};
